const SLIDE_IN_GROUP_ID = 'slide-in-group';
const DRAWER_HOST_ATTR = 'data-drawer-host';

let anonymousHostKeyCounter = 0;

/**
 * @returns {HTMLElement | null}
 */
export function getSlideInGroup() {
  return document.getElementById(SLIDE_IN_GROUP_ID);
}

/**
 * Returns a stable, unique key identifying a drawer host so its teleported panels
 * can be matched and replaced later.
 *
 * Hosts with an id or data-section-id use those directly. Anonymous hosts (e.g. the
 * release sign-up <drawer-component>, which renders once per breakpoint) previously
 * fell back to the tag name — so multiple instances collided on the same key and each
 * mount's replaceExisting pass removed the others' teleported panels, leaving all but
 * the last-mounted drawer with no panel in the DOM. Assign a unique key per element
 * instead, persisted on the element so repeat calls (reconcile) stay stable.
 *
 * @param {HTMLElement} host
 * @returns {string}
 */
export function getDrawerHostKey(host) {
  if (host.id) return host.id;
  if (host.dataset.sectionId) return host.dataset.sectionId;
  if (!host.dataset.drawerHostKey) {
    anonymousHostKeyCounter += 1;
    host.dataset.drawerHostKey = `${host.tagName.toLowerCase()}-${anonymousHostKeyCounter}`;
  }
  return host.dataset.drawerHostKey;
}

/**
 * Removes teleported drawer panels for a host from the slide-in group.
 * Used after section morphs replace in-host markup while stale panels remain teleported.
 *
 * @param {HTMLElement} host
 */
export function removeTeleportedPanels(host) {
  const slideInGroup = getSlideInGroup();
  if (!slideInGroup) return;

  const hostKey = getDrawerHostKey(host);
  slideInGroup.querySelectorAll(`[${DRAWER_HOST_ATTR}="${hostKey}"]`).forEach((element) => element.remove());
}

/**
 * Moves drawer panel nodes into the slide-in group so they escape the header stacking context.
 *
 * @param {HTMLElement} host
 * @param {{ replaceExisting?: boolean }} [options]
 * @returns {{ content: HTMLElement | null, backdrop: HTMLElement | null }}
 */
export function teleportDrawerPanels(host, options = {}) {
  const slideInGroup = getSlideInGroup();
  if (!slideInGroup) return { content: null, backdrop: null };

  const content = host.querySelector(':scope .drawer__content');

  // Only teleport drawers that have a content panel to relocate. Drawers without
  // .drawer__content (e.g. the shop-the-look drawer in product-card-badge.liquid, whose
  // panel uses .drawer and whose backdrop relies on group-open:* classes inherited from
  // its <details> ancestor) must stay in place: teleporting a lone backdrop strips those
  // classes — breaking the dim/click-away overlay — and, since such hosts share a host
  // key, each replaceExisting pass would also remove the others' teleported backdrops.
  if (!content) return { content: null, backdrop: null };

  const hostKey = getDrawerHostKey(host);

  if (options.replaceExisting) {
    removeTeleportedPanels(host);
  }

  const backdrop = host.querySelector(':scope .drawer__backdrop');

  if (content.parentElement !== slideInGroup) {
    content.classList.add(host.classList.contains('drawer__right') ? 'drawer__content--right' : 'drawer__content--left');
    content.setAttribute(DRAWER_HOST_ATTR, hostKey);
    slideInGroup.appendChild(content);
  }

  if (backdrop && backdrop.parentElement !== slideInGroup) {
    backdrop.setAttribute(DRAWER_HOST_ATTR, hostKey);
    slideInGroup.appendChild(backdrop);
  }

  return { content, backdrop };
}

/**
 * Keeps teleported drawer panels in sync with their details open state.
 *
 * @param {HTMLDetailsElement | null | undefined} details
 * @param {HTMLElement | null | undefined} content
 * @param {HTMLElement | null | undefined} backdrop
 * @returns {() => void}
 */
export function bindDrawerPanelState(details, content, backdrop) {
  if (!details || !content) return () => {};

  const sync = () => {
    const isOpen = details.hasAttribute('open');
    content.classList.toggle('drawer-panel--open', isOpen);
    backdrop?.classList.toggle('drawer-panel--open', isOpen);
  };

  details.addEventListener('toggle', sync);

  const observer = new MutationObserver(sync);
  observer.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });

  sync();

  return () => {
    details.removeEventListener('toggle', sync);
    observer.disconnect();
  };
}
